import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BrandingSettings, PublicBrandingResponse } from '@orion/contracts';
import { api, getCsrfToken, reportUnauthorizedResponse } from '../../lib/api-client.js';
import { useSettingsQuery } from '../admin/settings/api.js';

/** No session required — safe for the login screen and for `AppShell` to
 * call before/regardless of auth (the backend route has no guard). */
export function usePublicBrandingQuery() {
  return useQuery({
    queryKey: ['public-branding'],
    queryFn: () => api.get<PublicBrandingResponse>('/public/branding'),
    staleTime: 5 * 60_000,
  });
}

/** The admin form's own read of the full namespace (includes fields the
 * public response deliberately omits, e.g. `supportEmail`). */
export function useBrandingSettingsQuery() {
  return useSettingsQuery<BrandingSettings>('branding');
}

function invalidateBranding(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['public-branding'] });
  queryClient.invalidateQueries({ queryKey: ['admin-settings', 'branding'] });
}

async function uploadFile(path: string, file: File): Promise<BrandingSettings> {
  const form = new FormData();
  form.append('file', file);
  const csrfToken = getCsrfToken();
  const res = await fetch(`/api/v1${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: csrfToken ? { 'X-CSRF-Token': csrfToken } : undefined,
    body: form,
  });
  if (res.status === 401) reportUnauthorizedResponse();
  const payload = await res.json().catch(() => null);
  if (!res.ok) throw new Error(payload?.title ?? 'Upload failed');
  return payload.data as BrandingSettings;
}

export function useUploadLogo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadFile('/admin/settings/branding/logo', file),
    onSuccess: () => invalidateBranding(queryClient),
  });
}

export function useRemoveLogo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<BrandingSettings>('/admin/settings/branding/logo'),
    onSuccess: () => invalidateBranding(queryClient),
  });
}

export function useUploadLogoDark() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadFile('/admin/settings/branding/logo-dark', file),
    onSuccess: () => invalidateBranding(queryClient),
  });
}

export function useRemoveLogoDark() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<BrandingSettings>('/admin/settings/branding/logo-dark'),
    onSuccess: () => invalidateBranding(queryClient),
  });
}

export function useUploadFavicon() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadFile('/admin/settings/branding/favicon', file),
    onSuccess: () => invalidateBranding(queryClient),
  });
}

export function useRemoveFavicon() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<BrandingSettings>('/admin/settings/branding/favicon'),
    onSuccess: () => invalidateBranding(queryClient),
  });
}
